import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Phone, MapPin, Calendar, Clock, Filter, Search, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface Lead {
  _id: string;
  name: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  country: string;
  registeredDate: string;
  assignedDate: string;
  expiryDate: string;
  daysRemaining: number;
  status: 'active' | 'contacted' | 'interested' | 'not-interested' | 'expired';
  lastContactDate?: string;
  notes?: string;
  plan?: string;
}

interface LeadsStats {
  total: number;
  active: number;
  contacted: number;
  interested: number;
  notInterested: number;
  expired: number;
  expiringSoon: number;
}

export default function MarketingMyLeads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [leadStatus, setLeadStatus] = useState<Lead['status']>('contacted');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [stats, setStats] = useState<LeadsStats>({
    total: 0,
    active: 0,
    contacted: 0,
    interested: 0,
    notInterested: 0,
    expired: 0,
    expiringSoon: 0
  });

  // Filter leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           lead.phone.includes(searchTerm) ||
                           lead.city.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [leads, searchTerm, statusFilter]);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      console.log('🔍 Fetching my leads...');
      const response = await axios.get(`${API_URL}/leads/my-leads`);

      console.log('📊 My Leads Response:', {
        success: response.data.success,
        total: response.data.total,
        leadsCount: response.data.data?.length || 0
      });

      if (response.data.success) {
        setLeads(response.data.data);
        console.log('✅ Leads set in state:', response.data.data.length);
      }
    } catch (error: any) {
      console.error('Error fetching leads:', error);
      toast.error(error.response?.data?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.get(`${API_URL}/leads/my-leads/stats`);

      console.log('📊 My Leads Stats Response:', response.data);

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCallLead = (lead: Lead) => {
    // Simulate calling functionality
    window.open(`tel:${lead.phone}`);
    setSelectedLead(lead);
    setNotes(lead.notes || '');
    setLeadStatus(lead.status === 'active' ? 'contacted' : lead.status);
    setIsNotesDialogOpen(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedLead) return;

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.put(`${API_URL}/leads/my-leads/${selectedLead._id}`, {
        status: leadStatus,
        notes
      });

      if (response.data.success) {
        toast.success('Lead updated successfully');
        setIsNotesDialogOpen(false);
        setSelectedLead(null);
        setNotes('');
        fetchLeads();
        fetchStats();
      }
    } catch (error: any) {
      console.error('Error updating lead:', error);
      toast.error(error.response?.data?.message || 'Failed to update lead');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: Lead['status']) => {
    const variants: Record<Lead['status'], { variant: any; className: string; label: string }> = {
      active: { variant: 'outline', className: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Active' },
      contacted: { variant: 'outline', className: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Contacted' },
      interested: { variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200', label: 'Interested' },
      'not-interested': { variant: 'outline', className: 'bg-gray-50 text-gray-700 border-gray-200', label: 'Not Interested' },
      expired: { variant: 'secondary', className: '', label: 'Expired' },
    };

    const config = variants[status];
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  const getDaysRemainingBadge = (days: number) => {
    if (days <= 0) return <Badge variant="destructive">Expired</Badge>;
    if (days <= 3) return <Badge className="bg-orange-100 text-orange-700 border-orange-200">{days}d left</Badge>;
    return <Badge variant="outline" className="bg-gray-50">{days}d left</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-pink-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading your leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-gray-900 mb-2">My Assigned Leads</h1>
        <p className="text-gray-600">Manage and follow up with your assigned leads</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Leads</CardDescription>
            <CardTitle className="text-3xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active</CardDescription>
            <CardTitle className="text-3xl text-blue-600">{stats.active}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Contacted</CardDescription>
            <CardTitle className="text-3xl text-purple-600">{stats.contacted}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Interested</CardDescription>
            <CardTitle className="text-3xl text-green-600">{stats.interested}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Expiring Soon</CardDescription>
            <CardTitle className="text-3xl text-orange-600">{stats.expiringSoon}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by name, phone, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="not-interested">Not Interested</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <CardTitle>Leads List</CardTitle>
          <CardDescription>
            Contact leads and update their status before expiry
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Expiry</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No leads found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead._id}>
                      <TableCell>
                        <div>
                          <div>{lead.name}</div>
                          {lead.lastContactDate && (
                            <div className="text-xs text-gray-500">
                              Last contact: {new Date(lead.lastContactDate).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-gray-400" />
                          {lead.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          <div>
                            <div>{lead.city}</div>
                            <div className="text-xs text-gray-500">{lead.state}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={lead.plan === 'Free' ? 'bg-gray-50' : 'bg-green-50 text-green-700 border-green-200'}>
                          {lead.plan || 'Free'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center text-sm">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          {new Date(lead.registeredDate).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center text-sm">
                          <Clock className="h-4 w-4 mr-2 text-gray-400" />
                          {new Date(lead.assignedDate).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="text-sm">{new Date(lead.expiryDate).toLocaleDateString()}</div>
                          <div className="mt-1">{getDaysRemainingBadge(lead.daysRemaining)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(lead.status)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          onClick={() => handleCallLead(lead)}
                          disabled={lead.status === 'expired'}
                        >
                          <Phone className="h-4 w-4 mr-2" />
                          Call
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Notes Dialog */}
      <Dialog open={isNotesDialogOpen} onOpenChange={setIsNotesDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update Lead: {selectedLead?.name}</DialogTitle>
            <DialogDescription>
              Record your conversation and update the lead status
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="lead-status">Lead Status</Label>
              <Select value={leadStatus} onValueChange={(value: any) => setLeadStatus(value)}>
                <SelectTrigger id="lead-status" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contacted">
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2" />
                      Contacted
                    </div>
                  </SelectItem>
                  <SelectItem value="interested">
                    <div className="flex items-center">
                      <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                      Interested
                    </div>
                  </SelectItem>
                  <SelectItem value="not-interested">
                    <div className="flex items-center">
                      <XCircle className="h-4 w-4 mr-2 text-gray-600" />
                      Not Interested
                    </div>
                  </SelectItem>
                  <SelectItem value="active">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-blue-600" />
                      Active (Callback Later)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add notes about the conversation, next steps, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2 min-h-32"
              />
            </div>

            {selectedLead && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  <span>{selectedLead.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span>{selectedLead.city}, {selectedLead.state}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Days Remaining:</span>
                  <span>{getDaysRemainingBadge(selectedLead.daysRemaining)}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNotesDialogOpen(false)} disabled={updating}>
              Cancel
            </Button>
            <Button onClick={handleSaveNotes} disabled={updating}>
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                'Save & Update'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}